/*
 * The documents' public face in the browser (DV S-12 Appendix B, S-13 Appendix H): the types a page
 * that shows a document codes against, the view itself as a lazy component, the binding the boot
 * gives the documents, and *Text not saved* for the drive's Recent. Nothing heavy: this file is
 * reached by the drive's chunk and by the boot, and the editor, ProseMirror and the document's copy
 * arrive only with `DocumentView`'s own chunk (`entryClosure.test.ts`, `checkBudgets.ts`).
 */

import { lazy, type ReactNode } from "react"
import type { MenuItem } from "../../ui/index.ts"
import type { Request } from "../modules/registry.ts"
import type { ConnectionState } from "../store/model.ts"
import type { MentionCandidate, RefCandidate } from "./chips/AtMenu.tsx"
import type { UnsavedText } from "./collab/pending.ts"
import type { DocOpen, DocTabs } from "./wire/docs.gen.ts"

export type { AtSources, MentionCandidate, RefCandidate } from "./chips/AtMenu.tsx"
export type { UnsavedText } from "./collab/pending.ts"
export type { TabRow } from "./tabs/model.ts"
/** The document's words, for an owner drawing inside the view once its chunk registered them. */
export { t as docT } from "./i18n/t.ts"
export { listenToDocument } from "./live.ts"

/** Where the page opens the document (S-01 §3.3): a block, a heading, a thread, a version, a search. */
export interface DocumentAt {
	/** A block's id; a heading's id from the open answer's `headings`, or any block's through `…/locate`. */
	block?: string
	/** A heading's slug (`#h-<slug>`). */
	heading?: string
	thread?: number
	rev?: number
	find?: string
}

/**
 * What a page that shows a document gives the view: the document's place and name and what can be
 * done with it there. The drive is the first owner; a document outside the drive passes its own.
 */
export interface DocumentOwner {
	/** The document's name as its place has it; a *Text not saved* row shows it once the document is gone. */
	title: string
	/**
	 * The name is still the one the document was born with, never typed: the title field shows it
	 * empty under *Untitled document*, and the page names the document after its first line.
	 */
	titleProvisional?: boolean
	/**
	 * Gives the document another name: the title field's save, and the first line's while the name is
	 * provisional (`auto`). A refusal comes back as the place's error, which the field says.
	 */
	rename(name: string, options?: { auto?: boolean }): Promise<void>
	/** The band's place before the save state: the breadcrumb ending in the name's menu. */
	crumbs: ReactNode
	/**
	 * Asks before the reader's first write and holds the act until it is answered: a visitor by link
	 * gives its name at its first comment, suggestion or keystroke (W-1 D17, `visitor/`). `due` says
	 * whether the next write must ask; `ask(go)` asks, and runs `go` — the held act — once it is
	 * answered, and never when the question is dismissed.
	 */
	beforeWrite?: WriteGate
	/** The band's right side: faces, history, comments, Share. */
	band?: ReactNode
	/**
	 * The way back to where the document stands, at the head of the contents panel (DS2-D7): the
	 * folder's name ("‹ Plans"), its address, and the link's accessible name in the place's own words
	 * ("Open the folder Plans"). Everything else beside the text is the view's own panel, the same for
	 * every reader (DS2-D1); a page without a place — a link holder's, a visitor's — starts it with
	 * the document's tabs and outline.
	 */
	place?: { name: string; href: string; label: string }
	/** Each open answer as it is read: a page that must know something of the record looks there. */
	onOpen?: (open: DocOpen) => void
	/**
	 * An open the server refused: the place drops a `?tab=` its address names that no longer opens —
	 * a key no tab has, or one removed or purged meanwhile —, so *Try again* is never the only way out.
	 */
	onOpenFailed?: (error: unknown) => void
	/**
	 * The open document's tab map, each time it is read: the place checks the tab its address names
	 * (`?tab=`) against the map's `main` and keeps its own address canonical (W-2 §3.2).
	 */
	onTabs?: (tabs: DocTabs) => void
	/** The entries of the name's menu that are the place's: a copy, a move, a star, Details, the trash. */
	nameMenuEntries?: readonly NameMenuEntry[]
	/** Where a new document lives and who can open it, under its title (S-01 §3.5). */
	accessLine?: ReactNode
	/**
	 * What the place says over a document in its trash — who moved it there, when, and when it is
	 * deleted for good (DS-3 §2) —, beside the line's **Restore**. Without it the line says only that
	 * the document is in the trash.
	 */
	trashedLine?: ReactNode
	/**
	 * What the place says once under the title when the document is new to the reader in some other
	 * way: a file just opened as a document, with what came over and what did not (S-01-D46).
	 */
	banner?: ReactNode
	/** A document's name and address by its key, or null when the reader cannot open it. */
	describeLink(key: string): Promise<{ name: string; url: string } | null>
	/**
	 * Puts files that are no picture or video into the document's folder (S-17 §3.9): the folder's
	 * name and the items made, which the text then links as item chips. Without it such a file cannot
	 * be added here.
	 */
	uploadFiles?: (files: readonly File[]) => Promise<{
		folder: string
		items: readonly { key: string; name: string }[]
	}>
	/**
	 * The people the `@` menu offers for a query (S-13 Appendix H, S-17 §3.10): those who can read the
	 * document first, others marked, ranked as the composer ranks a mention. Without it the menu lists
	 * no people.
	 */
	mentionCandidates?: (query: string) => Promise<readonly MentionCandidate[]>
	/**
	 * The documents, files and folders the `@` menu offers for a query: the ones beside this document
	 * first, then the rest the reader can open. Without it the menu lists no items.
	 */
	refCandidates?: (query: string) => Promise<readonly RefCandidate[]>
	/** The document's place is archived: the text is read-only for everybody. */
	archived?: boolean
	/**
	 * Makes a new document beside this one from text this device kept: a restored server's newer
	 * text, or typing the server keeps refusing. Without it the page offers Copy alone.
	 */
	newDocument?(from: { tree: unknown } | { text: string }): Promise<void>
	/**
	 * Makes a new document beside this one from revision `rev` of it: Version history's *Make a copy*
	 * (S-05 §3.8), which goes through the place as every copy does. Without it no copy is offered there.
	 */
	copyVersion?(rev: number): Promise<void>
	/**
	 * Takes the document out of the trash: the trash line's **Restore**, after which the view opens the
	 * document again. It rejects when the place refused, having said why. Without it no Restore is
	 * offered.
	 */
	restore?(): Promise<void>
	/**
	 * Goes to an address of this application: a tab's (`?tab=`, a new entry), a heading's or a block's
	 * of the open tab (`#…`, in place of the entry on screen), any other page's.
	 */
	onNavigate(path: string, options?: NavigateOptions): void
}

/** What `DocumentOwner.beforeWrite` asks of a page: whether a write must wait, and the asking. */
export interface WriteGate {
	due(): boolean
	ask(go: () => void): void
}

/**
 * The entries of the name's menu, in the order S-01 §3.5 lists them. The view brings its own (Rename,
 * Copy link, and those of later packages); a place brings the rest, and the menu sorts both by this
 * one list, so neither side decides where an entry stands.
 */
export const NAME_MENU_ORDER = [
	// A document in the trash leads with the way out of it (DS-3 §2).
	"restore",
	"rename",
	// A visitor's own (S-01 §3.20): its name until its first write, and the way to sign in.
	"changeName",
	"signIn",
	"addTab",
	"removedTabs",
	"history",
	"export",
	"pageWidth",
	"copyLink",
	"copyMarkdown",
	"copy",
	"move",
	"star",
	"details",
	"wordCount",
	"trash",
	// A trashed document's last entry, for a team member of its drive (DS3-D7).
	"deleteForGood",
] as const

export type NameMenuEntryId = (typeof NAME_MENU_ORDER)[number]

/**
 * One entry of the name's menu. Its words are the menu's (`doc.name.<id>`), so a place names what it
 * offers and never spells it.
 */
export interface NameMenuEntry {
	id: NameMenuEntryId
	onSelect: () => void
	/** A toggle that is on: *Remove from starred* in place of *Add to starred*. */
	on?: boolean
	/** A destructive entry: red ink. */
	danger?: boolean
	/** Kept for a reader who may only read (S-01 §3.5: Version history, Export, Copy link …). */
	forReaders?: boolean
	/** An entry that opens its own menu beside it (*Export ▸*); its `onSelect` is not called. */
	submenu?: { heading?: string; items: MenuItem[] }
}

/**
 * Where every document lives in the browser: `/drive/<link id>` — and `/drive/<key>`, which a member's
 * page moves to it —, a trailing `/<slug>` ignored (DV S-06-D5, W-1 D1).
 */
export const DOCUMENT_PATH_PREFIX = "/drive"

/** A document's key: eight symbols of `core/keys`' look-alike-free alphabet. */
const DOCUMENT_KEY = /^[2-9a-hjkmnp-z]{8}$/

/**
 * docKeyFromPath reads the key a document link names — a path `/drive/<key>`, with a `/<slug>`, a
 * query or a fragment after it, or the whole link on this origin — and answers null for anything else:
 * another origin, another place, a fixed page of the drive. The key may name a folder or a file too;
 * whether it is a document the reader can open is the owner's `describeLink`.
 */
export function docKeyFromPath(
	link: string,
	origin: string = window.location.origin,
): string | null {
	let path: string
	try {
		const url = new URL(link, origin)
		if (url.origin !== origin) return null
		path = url.pathname
	} catch {
		return null
	}
	const [first, prefix, segment] = path.split("/")
	if (first !== "" || `/${prefix ?? ""}` !== DOCUMENT_PATH_PREFIX) return null
	let key: string
	try {
		key = decodeURIComponent(segment ?? "")
	} catch {
		return null
	}
	return DOCUMENT_KEY.test(key) ? key : null
}

/**
 * The place an address's fragment opens a document at (S-01 §3.3): `#h-<slug>` a heading by its words,
 * `#c-<id>` a comment's thread, anything else a block by its id. An empty fragment names none.
 */
export function documentAtOfHash(hash: string): DocumentAt | undefined {
	let raw: string
	try {
		raw = decodeURIComponent(hash.replace(/^#/, ""))
	} catch {
		return undefined
	}
	if (raw === "") return undefined
	if (raw.startsWith("h-") && raw.length > 2) return { heading: raw.slice(2) }
	if (raw.startsWith("c-")) {
		const thread = Number(raw.slice(2))
		return Number.isSafeInteger(thread) && thread > 0 ? { thread } : undefined
	}
	return { block: raw }
}

/**
 * The open tab's outline (S-13 Appendix H): its H1–H3 in document order, each with its block id (null
 * for a heading made this visit and not yet named by the server), its level, words, `#h-<slug>`, its
 * position when the outline was read, and whether it is folded. Which heading the reader is at is not
 * the outline's: the contents panel follows the scroll for that (`contents/useCurrentHeading.ts`).
 */
export interface OutlineState {
	headings: readonly OutlineHeading[]
}

/** One heading of `OutlineState`. */
export interface OutlineHeading {
	id: string | null
	level: 1 | 2 | 3
	text: string
	slug: string
	pos: number
	folded: boolean
}

/**
 * How `DocumentOwner.onNavigate` goes to an address: by default a new history entry; `replace` puts
 * the address in place of the one on screen, as a jump to a heading of the open tab does (DS2-D11),
 * so Back leaves the document rather than walking its headings.
 */
export interface NavigateOptions {
	replace?: boolean
}

export interface DocumentViewProps {
	docKey: string
	owner: DocumentOwner
	at?: DocumentAt
}

/** The document page's view, fetched with its own chunk the first time a document opens. */
export const DocumentView = lazy(() => import("./DocumentView.tsx"))

/** What the boot hands the documents: the reader, the shell's socket request and its connection. */
export interface DocumentBindings {
	user: () => number | null
	/**
	 * The reader is a visitor by link (W-1 D6), whose page binds the documents itself (`visitor/`):
	 * its id, beside `user` 0, which makes every draft and caret this visitor's own; and `named`,
	 * which a document asks before it opens a session to write in, since nothing a visitor writes
	 * goes without its name (D17).
	 */
	visitor?: { id: number; named: () => Promise<void> }
	request: Request
	connection: (on: (state: ConnectionState) => void) => () => void
}

let bound: DocumentBindings | null = null

/**
 * bindDocuments is the boot's one call for documents, beside the modules' bindings: every view opened
 * later sends through it. It also starts the flusher that brings the unconfirmed steps of a tab that
 * closed to the server (S-03-D16), again after every reconnect; the collab core and the schema load
 * only when this device holds such a row.
 */
export function bindDocuments(b: DocumentBindings): () => void {
	bound = b
	let flusher: { kick(): void; stop(): void } | null = null
	let stopped = false
	const user = b.user()
	// The connection tells every change of the store, not only of its state: the flusher is kicked
	// when the state becomes "open", once per reconnect, never on each notification while open.
	let last: ConnectionState | null = null
	const unwatch = b.connection((state) => {
		if (state === "open" && last !== "open") flusher?.kick()
		last = state
	})
	// A visitor's page flushes nothing headless: every visitor is user 0, and a row the open cannot
	// carry is put into that page's Kept text instead (openEditor), where the visitor sees it.
	if (user !== null && b.visitor === undefined) {
		void import("./collab/pending.ts").then(({ startOrphanFlusher }) => {
			if (stopped) return
			flusher = startOrphanFlusher({
				userId: user,
				flush: async (tab) => {
					const [{ docLink, flushRow }, { schema }] = await Promise.all([
						import("./collab/socket.ts"),
						import("./schema/schema.ts"),
					])
					const link = docLink({
						doc: tab.docId,
						key: tab.docKey,
						user,
						send: b.request,
						connection: b.connection,
					})
					return flushRow(tab, link, schema, user)
				},
			})
		})
	}
	return () => {
		stopped = true
		unwatch()
		flusher?.stop()
		bound = null
	}
}

/** The boot's bindings, or null before `bindDocuments` (a view then waits offline). */
export function documentBindings(): DocumentBindings | null {
	return bound
}

/** *Text not saved* (S-01-D60): the reader's text this device could not save, newest document first. */
export async function listUnsaved(userId: number): Promise<UnsavedText[]> {
	return (await import("./collab/pending.ts")).listUnsaved(userId)
}

/** The person's **Throw away** on a *Text not saved* row: the only way such text leaves the device. */
export async function discardUnsaved(userId: number, docKey: string): Promise<void> {
	return (await import("./collab/pending.ts")).discardUnsaved(userId, docKey)
}
